import { FormEvent, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowDownToLine, Send, ArrowUpRight, ArrowDownLeft } from 'lucide-react';
import { api } from '../api/client';
import { TopBar } from '../components/layout/TopBar';
import { Card } from '../components/ui/Card';
import { StatCard } from '../components/ui/StatCard';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { Badge } from '../components/ui/Badge';
import { fmt } from '../lib/format';

interface Wallet { usdtBalance: string; cpBalance: string; rpBalance: string }
interface Tx {
  id: string;
  type: string;
  asset: string;
  amount: string;
  genLevel: number | null;
  counterparty: string | null;
  createdAt: string;
}

export function WalletPage() {
  const [params, setParams] = useSearchParams();
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [txs, setTxs] = useState<Tx[]>([]);
  const [modal, setModal] = useState<null | 'deposit' | 'transfer'>(null);

  async function refresh() {
    const [w, t] = await Promise.all([
      api<Wallet>('/wallet'),
      api<{ transactions: Tx[] }>('/wallet/transactions?limit=40'),
    ]);
    setWallet(w); setTxs(t.transactions);
  }

  useEffect(() => { refresh().catch(() => { /* ignore */ }); }, []);

  useEffect(() => {
    const action = params.get('action');
    if (action === 'deposit' || action === 'transfer') {
      setModal(action);
      params.delete('action');
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  return (
    <>
      <TopBar title="Wallet" />
      <div className="px-4 pt-4 space-y-4">
        {/* Hero */}
        <Card glow>
          <div className="text-[10px] font-semibold tracking-wider uppercase text-fg-muted">USDT Balance</div>
          <div className="mt-1 text-[40px] leading-none font-extrabold font-mono text-accent">
            {wallet ? fmt(wallet.usdtBalance) : '—'}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <StatCard label="CP (Purchase)" value={fmt(wallet?.cpBalance ?? 0)} accent="cp" />
            <StatCard label="RP (Activate)" value={fmt(wallet?.rpBalance ?? 0)} accent="rp" />
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <Button onClick={() => setModal('deposit')} size="md" fullWidth>
              <ArrowDownToLine size={16} /> Deposit
            </Button>
            <Button onClick={() => setModal('transfer')} size="md" variant="secondary" fullWidth>
              <Send size={16} /> Transfer
            </Button>
          </div>
        </Card>

        {/* Transactions */}
        <Card padded={false}>
          <div className="p-4 pb-2 flex items-center justify-between">
            <div className="text-sm font-bold">Recent Transactions</div>
            <Badge>{txs.length}</Badge>
          </div>
          {txs.length === 0 ? (
            <div className="p-6 text-center text-sm text-fg-muted">No transactions yet</div>
          ) : (
            <ul className="divide-y divide-border">
              {txs.map((t) => <TxRow key={t.id} tx={t} />)}
            </ul>
          )}
        </Card>
      </div>

      <DepositModal open={modal === 'deposit'} onClose={() => { setModal(null); refresh(); }} />
      <TransferModal open={modal === 'transfer'} onClose={() => { setModal(null); refresh(); }} />
    </>
  );
}

function TxRow({ tx }: { tx: Tx }) {
  const positive = !tx.amount.startsWith('-');
  const amt = tx.amount.replace(/^-/, '');
  return (
    <li className="p-3.5 flex items-center gap-3">
      <span className={`w-9 h-9 rounded-full flex items-center justify-center ${positive ? 'bg-success/15 text-success' : 'bg-danger/15 text-danger'}`}>
        {positive ? <ArrowDownLeft size={18} /> : <ArrowUpRight size={18} />}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold truncate">
          {prettyType(tx.type)}{tx.genLevel ? <span className="text-fg-muted"> · G{tx.genLevel}</span> : null}
        </div>
        <div className="text-[11px] text-fg-muted">{new Date(tx.createdAt).toLocaleString()}</div>
      </div>
      <div className="text-right">
        <div className={`text-sm font-bold font-mono ${positive ? 'text-success' : 'text-danger'}`}>
          {positive ? '+' : '−'}{fmt(amt)}
        </div>
        <div className="text-[10px] uppercase tracking-wider text-fg-muted">{tx.asset}</div>
      </div>
    </li>
  );
}

function prettyType(t: string) {
  return t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function DepositModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault(); setBusy(true); setErr(null);
    try {
      await api('/wallet/deposit', { method: 'POST', body: JSON.stringify({ amount }), signed: true });
      setAmount(''); onClose();
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }

  return (
    <Modal open={open} onClose={onClose} title="Deposit USDT → RP">
      <form onSubmit={submit} className="space-y-4">
        <div className="p-3 rounded-xl bg-accent/5 border border-accent/20 text-xs text-fg-muted">
          100% of your deposited USDT is credited as <span className="text-accent font-semibold">RP</span>, which you use to activate purchased packages.
        </div>
        <Input
          label="Amount (USDT)"
          type="number"
          step="0.01"
          min="1"
          placeholder="100"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          required
          autoFocus
        />
        {err && <div className="text-sm text-danger">{err}</div>}
        <Button type="submit" fullWidth size="lg" loading={busy}>Confirm Deposit</Button>
      </form>
    </Modal>
  );
}

function TransferModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [toUsername, setToUsername] = useState('');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault(); setBusy(true); setErr(null);
    try {
      await api('/wallet/transfer', { method: 'POST', body: JSON.stringify({ toUsername, amount }), signed: true });
      setToUsername(''); setAmount(''); onClose();
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }

  return (
    <Modal open={open} onClose={onClose} title="Transfer USDT → CP">
      <form onSubmit={submit} className="space-y-4">
        <div className="p-3 rounded-xl bg-accent/5 border border-accent/20 text-xs text-fg-muted">
          You spend USDT. Recipient receives 100% as <span className="text-accent font-semibold">CP</span> (used to purchase packages).
        </div>
        <Input label="Recipient username" value={toUsername} onChange={(e) => setToUsername(e.target.value)} required autoFocus />
        <Input label="Amount (USDT)" type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} required />
        {err && <div className="text-sm text-danger">{err}</div>}
        <Button type="submit" fullWidth size="lg" loading={busy}>Send Transfer</Button>
      </form>
    </Modal>
  );
}
